import Papa from "papaparse";

/** Same normalization as column suggestion: case and surrounding spaces do not make a different header. */
const normalizeCsvHeader = (value: string) => value.trim().toLowerCase();

/**
 * Header names that appear more than once.
 * Blank cells are ignored: a trailing delimiter must not block the file.
 *
 * Without `singleValueHeaders`, comparison matches the parser (exact string).
 * Sales and stock files cannot represent a repeated header, so every duplicate is rejected.
 *
 * With `singleValueHeaders`, only those names are rejected. Comparison is normalized
 * so `titre` and `Titre ` still count as the article name. Channel columns, and any
 * header the publisher named themselves, may repeat.
 */
export const findDuplicateCsvHeaders = (
    headers: readonly string[],
    options?: { singleValueHeaders?: readonly string[] },
): string[] => {
    const singleValue = options?.singleValueHeaders
        ? new Set(options.singleValueHeaders.map((header) => normalizeCsvHeader(String(header))))
        : null;
    const counts = new Map<string, { count: number; label: string }>();

    for (const header of headers) {
        const name = header == null ? "" : String(header);
        if (!name.trim()) continue;
        const key = singleValue ? normalizeCsvHeader(name) : name;
        if (singleValue && !singleValue.has(key)) continue;

        const current = counts.get(key);
        if (current) {
            current.count += 1;
            continue;
        }
        counts.set(key, { count: 1, label: singleValue ? name.trim() : name });
    }

    return [...counts.values()].filter((entry) => entry.count > 1).map((entry) => entry.label);
};

/** First CSV row only, so a duplicate header can be rejected before the file is fully parsed. */
export const readCsvHeaders = (file: File, onHeaders: (headers: string[]) => void) => {
    Papa.parse<string[]>(file, {
        preview: 1,
        skipEmptyLines: true,
        complete: (preview) => {
            onHeaders(preview.data[0] ?? []);
        },
    });
};
